import { useCallback, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { i18n } from '../lib/i18n'
import { AuthenticationActions } from '../redux/authentication/actions'
import { NotificationActions } from '../redux/notification/action'
import { tokenSelector } from '../redux/notification/selector'
import { RootState } from '../redux/reducers'
import { showDanger } from '../services/toast'
import { ERROR_CODES, userAuthentication } from '../services/userAuthentication'

interface SignInError {
  status: number
  code: string
  message: string
}

const handleSignInError = (error: SignInError, withCode: boolean) => {
  console.warn(error.message)
  if ([ERROR_CODES.APPLE_USER_CANCELED, ERROR_CODES.GOOGLE_USER_CANCELED].includes(error.code)) {
    return
  }
  if (withCode && error.status === 400) {
    showDanger(i18n.t('signIn.invalidCode'), 3000)
    return
  }
  if (error.code === 'auth/wrong-password' || error.code === 'auth/user-not-found' || error.status === 401) {
    showDanger(i18n.t('signIn.wrongCredentials'), 3000)
    return
  }
  if (error.code === 'auth/account-exists-with-different-credential') {
    showDanger(i18n.t('signIn.accountExistWithExistingCredential'), 3000)
    return
  }
  console.error(error)
  showDanger()
}

export const useSignIn = () => {
  const dispatch = useDispatch()
  const firebaseToken = useSelector((state: RootState) => tokenSelector(state))
  const [loading, setLoading] = useState<boolean>(false)

  const doSignIn = useCallback(async (email, password, code?: string | null) => {
    setLoading(true)
    try {
      let token
      if (!code) token = await userAuthentication.signIn(email, password)
      else token = await userAuthentication.signInWith2fa(email, password, code)
      if (token.code === 'ERRAUTH002') {
        dispatch(AuthenticationActions.set2fa(true))
      } else {
        dispatch(AuthenticationActions.set2fa(false))
        dispatch(AuthenticationActions.storeIsConnected(!!token))
        dispatch(AuthenticationActions.storeApiToken(token.accessToken, token.tokenType, token.refreshToken))
        dispatch(NotificationActions.sendToken(firebaseToken, true))
        return true
      }
    } catch (error) {
      handleSignInError(error as SignInError, !!code)
    } finally {
      setLoading(false)
    }
  }, [])

  return { doSignIn, loading }
}
